import React, {
  ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import MemoizedSection from '../../../../pages/edit/components/MemoizedSection';
import {
  ControlField,
  FieldMetadata,
} from '../../../../contexts/MetadataContext';
import { ListingResource } from '../../../../system/field';
import { ResourceField } from '../../hooks/useResourceForm/useResourceForm';
import { Buttons } from '../../../../constants/Buttons';
import {
  isValidFromDefinition,
  runCustomValidation,
} from '../../../../utils/validation';
import {
  useResourceEditor,
  type EditableField,
} from '@brightmls/nextgen.lae.flc';

interface ResourceFormProps {
  resourceType: ListingResource;
  fields: ResourceField[];
  initialValues?: any;
  resourceIndex?: number | Function;
  onSave: (values: any, saveAndAddAnother: boolean) => Promise<void>;
  saveAndAddAnother: boolean;
  onCancel: () => void;
  getBaseFieldMetadata: (key: string) => FieldMetadata;
  lookupValueForListingField: (lookupKey: string, value: any) => string;
  displayValueForListingField: (lookupKey: string, value: any) => string;
  formHeader?: ReactElement;
  formStyles?: any;
  saveHook?: (resource: any, isCreate: boolean) => any | false;
  isAddition: boolean;
}

const ResourceForm: React.FC<ResourceFormProps> = ({
  resourceType,
  fields,
  initialValues,
  resourceIndex,
  onSave,
  saveAndAddAnother,
  getBaseFieldMetadata,
  lookupValueForListingField,
  displayValueForListingField,
  formHeader,
  formStyles = {},
  saveHook,
  isAddition,
}) => {
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [isSaving, setIsSaving] = useState(false);

  // Convert ResourceField format to EditableField format for FLC
  const editableFields: EditableField[] = useMemo(
    () =>
      fields.map((field) => ({
        Key: field.Key,
        validationRules: field.validationRules,
        ForceDisplay: field.ForceDisplay,
      })),
    [fields],
  );

  // Normalize initial values for lookup fields
  const normalizedInitialValues = useMemo(() => {
    if (!initialValues) return initialValues;

    const normalized = { ...initialValues };

    fields.forEach((field) => {
      const metadata = getBaseFieldMetadata(field.Key);
      const fieldValue = initialValues[field.Key];

      // If field has a lookup and has a value, normalize it
      if (metadata?.Lookup && fieldValue) {
        normalized[field.Key] = lookupValueForListingField(
          metadata.Lookup,
          fieldValue,
        );
      }
    });

    return normalized;
  }, [initialValues, fields, getBaseFieldMetadata, lookupValueForListingField]);

  // Calculate the actual index
  const calculatedIndex = useMemo(() => {
    if (typeof resourceIndex === 'function') {
      return resourceIndex();
    }
    return resourceIndex;
  }, [resourceIndex]);

  // Use the new FLC hook with normalized initial values
  const editor = useResourceEditor(resourceType, {
    index: calculatedIndex,
    initialValues: normalizedInitialValues,
    fields: editableFields,
    validateOnChange: true,
  });

  // Create the merged field metadata getter with proper undefined handling
  const getFieldMetadata = useCallback(
    (key: string) => {
      const field = fields.find((f) => f.Key === key);
      const baseMetadata = getBaseFieldMetadata(key);

      if (field?.Metadata) {
        // Create a new object starting with base metadata
        const mergedMetadata: any = { ...baseMetadata };

        // Explicitly handle each property in field.Metadata
        Object.entries(field.Metadata).forEach(([metaKey, metaValue]) => {
          if (metaValue === undefined) {
            // If the value is explicitly undefined, delete the property
            delete mergedMetadata[metaKey];
          } else {
            // Otherwise, set the value
            mergedMetadata[metaKey] = metaValue;
          }
        });

        return mergedMetadata;
      }

      return baseMetadata;
    },
    [fields, getBaseFieldMetadata],
  );

  // Check for errors using field definitions and FLC validation
  const hasErrors = useMemo(() => {
    return fields.some((field) => {
      const fieldValidation = editor.validation[field.Key] || {};
      const fieldValue = editor.values[field.Key];
      const isFieldHidden = editor.isFieldHidden(field.Key);

      if (isFieldHidden) {
        return false;
      }

      const isRequired =
        (field.SkipRequiredIfReadOnly && fieldValidation.isReadOnly) ||
        field.SkipRequired
          ? false
          : field.ForceRequired || fieldValidation.isRequired;

      let errorList = fieldValidation?.errors || [];

      if (field.SkipRequired) {
        errorList = errorList.filter(
          (message) => !message.includes('require') && !message.includes('n/a'),
        );
      }

      let isError =
        errorList.length > 0 ||
        (isRequired &&
          (fieldValue === undefined ||
            fieldValue === null ||
            fieldValue === ''));

      if (!isError) {
        const metadata = getFieldMetadata(field.Key);

        // Only validate if metadata exists and has a Definition
        if (metadata && metadata.Definition) {
          // Normalize lookup values before validation (safety check)
          // Initial values are already normalized, but this handles any runtime changes
          let normalizedValue = fieldValue;
          if (metadata.Lookup && fieldValue) {
            normalizedValue = lookupValueForListingField(
              metadata.Lookup,
              fieldValue,
            );
          }

          let validation = isValidFromDefinition(
            field as ControlField,
            metadata as FieldMetadata,
            normalizedValue,
          );

          if (validation.isValid) {
            // Create a getter that matches the old signature
            const getFieldValue = (key: string, fallback: any) => {
              if (key === '$') return editor.values;
              return editor.values[key] ?? fallback;
            };

            validation = runCustomValidation(
              field as ControlField,
              normalizedValue,
              getFieldValue,
            );
          }

          isError = !validation.isValid;
        }
      }

      return isError;
    });
  }, [
    fields,
    editor.validation,
    editor.values,
    editor.isFieldHidden,
    getFieldMetadata,
    lookupValueForListingField,
  ]);

  // Create field context that matches the expected interface
  const fieldContext = useMemo(
    () => ({
      getFieldValue: (key: string, fallback: any) => {
        if (key === '$') return editor.values;
        return editor.values[key] ?? fallback;
      },
      updateField: editor.updateField,
      getFieldPresentation: (_: string, fieldName: string) => {
        const field = fields.find((f) => f.Key === fieldName);
        const presentation = {
          ...(editor.validation[fieldName] || {
            isHidden: false,
            isRequired: false,
            isReadOnly: false,
            isLocked: false,
            errors: [],
            warnings: [],
            messages: [],
          }),
          isFocused: false,
          isHighlighted: false,
        };

        if (field?.SkipRequired) {
          // Skip the required field: always reject rule declaration of being required
          presentation.isRequired = false;
        }

        return presentation;
      },
      getRemoveFromList: (_: string, fieldName: string) =>
        editor.getRemoveFromList(fieldName),
      isFieldHidden: (_: string, fieldName: string) =>
        editor.isFieldHidden(fieldName),
      getFieldMetadata,
      resourceType,
    }),
    [editor, getFieldMetadata, resourceType],
  );

  // Denormalize values before saving (convert LookupValue back to DisplayValue)
  const denormalizeValues = useCallback(
    (values: any) => {
      const denormalized = { ...values };

      fields.forEach((field) => {
        const metadata = getBaseFieldMetadata(field.Key);
        const fieldValue = values[field.Key];

        // If field has a lookup and has a value, denormalize it
        if (metadata?.Lookup && fieldValue) {
          denormalized[field.Key] = displayValueForListingField(
            metadata.Lookup,
            fieldValue,
          );
        }
      });

      return denormalized;
    },
    [fields, getBaseFieldMetadata, displayValueForListingField],
  );

  const handleSave = async (saveAndAddAnother: boolean) => {
    if (isSaving) {
      return;
    }
    setIsSaving(true);
    try {
      // Denormalize lookup values before saving
      const denormalizedValues = denormalizeValues(editor.values);
      await onSave(denormalizedValues, saveAndAddAnother);
    } catch (error) {
      console.error('Error saving form:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          p: 2,
          maxHeight: 'calc(100vh - 138px)',
          overflowY: 'auto',
          ...formStyles,
        }}
      >
        {!!formHeader && <Box>{formHeader}</Box>}
        <MemoizedSection
          section={{
            name: '',
            fields: fields as ControlField[],
          }}
          groupIndex={0}
          sectionIndex={0}
          groupings={[]}
          totalSections={1}
          onCustomControlLoaded={() => {}}
          groupName={resourceType}
          sectionRefs={sectionRefs}
          fieldContext={fieldContext}
          lockLayout={true}
        />
        {saveAndAddAnother && (
          <Button
            variant="outlined"
            onClick={() => handleSave(true)}
            disabled={isSaving || hasErrors}
            sx={{
              textTransform: 'none',
              ...Buttons.primaryOutlined,
              '&.Mui-disabled': {},
            }}
          >
            {isSaving ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Saving...
              </>
            ) : (
              'Save And Add Another'
            )}
          </Button>
        )}
      </Box>

      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          gap: 2,
        }}
      >
        <Button
          fullWidth
          variant="contained"
          onClick={() => handleSave(false)}
          disabled={isSaving || hasErrors}
          sx={{
            textTransform: 'none',
            ...Buttons.primary,
            '&.Mui-disabled': {
              backgroundColor: 'grey.300',
              color: 'white',
            },
          }}
        >
          {isSaving ? (
            <>
              <CircularProgress
                size={20}
                sx={{
                  mr: 1,
                  color: 'white',
                }}
              />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default ResourceForm;
